import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Trash2, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface LineItem {
  description: string;
  item_type: string;
  amount: string;
  quantity: number;
}

interface Props {
  children: { id: string; full_name: string; monthly_fee?: number; enrollment_date?: string }[];
  feeTemplates: { id: string; name: string; amount: number; fee_type: string }[];
  onSuccess: () => void;
}

const emptyItem = (): LineItem => ({ description: "", item_type: "subscription", amount: "", quantity: 1 });

export default function AddInvoiceDialog({ children, feeTemplates, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [childId, setChildId] = useState("");
  const [title, setTitle] = useState("");
  const [feeType, setFeeType] = useState("monthly");
  const [dueDate, setDueDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyItem()]);
  const [isProrata, setIsProrata] = useState(false);
  const [prorataInfo, setProrataInfo] = useState<string | null>(null);

  const handleTemplateSelect = (templateId: string) => {
    const t = feeTemplates.find((f) => f.id === templateId);
    if (t) {
      setTitle(t.name);
      setFeeType(t.fee_type);
      setLineItems([{ description: t.name, item_type: t.fee_type === "monthly" ? "subscription" : "activity", amount: t.amount.toString(), quantity: 1 }]);
    }
  };

  const addLineItem = () => setLineItems([...lineItems, emptyItem()]);
  const removeLineItem = (idx: number) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateLineItem = (idx: number, field: keyof LineItem, value: string | number) => {
    const updated = [...lineItems];
    (updated[idx] as any)[field] = value;
    setLineItems(updated);
  };

  const totalAmount = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0) * item.quantity, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.nursery_id || !childId || !title || totalAmount <= 0) return;
    setLoading(true);

    const { data: invoice, error } = await supabase.from("invoices").insert({
      nursery_id: profile.nursery_id,
      child_id: childId,
      title,
      amount: totalAmount,
      fee_type: feeType,
      due_date: dueDate,
      notes: notes || null,
    }).select("id").single();

    if (error) {
      toast.error("خطأ: " + error.message);
      setLoading(false);
      return;
    }

    // Insert line items
    if (invoice && lineItems.length > 0) {
      const items = lineItems.filter(li => li.description && parseFloat(li.amount) > 0).map(li => ({
        invoice_id: invoice.id,
        description: li.description,
        item_type: li.item_type,
        amount: parseFloat(li.amount),
        quantity: li.quantity,
      }));
      if (items.length > 0) {
        await supabase.from("invoice_items").insert(items);
      }
    }

    setLoading(false);
    toast.success("تم إنشاء الفاتورة بنجاح");
    setOpen(false);
    setChildId("");
    setTitle("");
    setNotes("");
    setLineItems([emptyItem()]);
    setIsProrata(false);
    setProrataInfo(null);
    onSuccess();
  };

  const itemTypeOptions = [
    { value: "subscription", label: "اشتراك" },
    { value: "educational", label: "تعليمي" },
    { value: "activity", label: "نشاط إضافي" },
    { value: "other", label: "أخرى" },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Plus className="w-4 h-4 ml-2" />
          إنشاء فاتورة
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>إنشاء فاتورة جديدة</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>الطفل *</Label>
            <Select value={childId} onValueChange={setChildId}>
              <SelectTrigger><SelectValue placeholder="اختر الطفل" /></SelectTrigger>
              <SelectContent>
                {children.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {feeTemplates.length > 0 && (
            <div>
              <Label>اختر من القوالب</Label>
              <Select onValueChange={handleTemplateSelect}>
                <SelectTrigger><SelectValue placeholder="اختر قالب رسوم..." /></SelectTrigger>
                <SelectContent>
                  {feeTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} - {t.amount} ج.م</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>عنوان الفاتورة *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="رسوم شهر يناير" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>نوع الرسوم</Label>
              <Select value={feeType} onValueChange={setFeeType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">شهرية</SelectItem>
                  <SelectItem value="additional">إضافية</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>تاريخ الاستحقاق</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          {/* Pro-rata Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-primary" />
              <div>
                <Label className="text-sm font-medium">فاتورة جزئية (Pro-rata)</Label>
                <p className="text-xs text-muted-foreground">حساب المبلغ حسب الأيام المتبقية من الشهر</p>
              </div>
            </div>
            <Switch checked={isProrata} onCheckedChange={(checked) => {
              setIsProrata(checked);
              if (checked && childId) {
                const child = children.find(c => c.id === childId);
                if (child?.monthly_fee && child.monthly_fee > 0) {
                  const now = new Date();
                  const enrollDate = child.enrollment_date ? new Date(child.enrollment_date) : now;
                  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                  const dayOfMonth = enrollDate.getDate();
                  const remainingDays = daysInMonth - dayOfMonth + 1;
                  const amount = Math.round((child.monthly_fee / daysInMonth) * remainingDays * 100) / 100;
                  setProrataInfo(`${child.monthly_fee} ÷ ${daysInMonth} يوم × ${remainingDays} يوم = ${amount} ج.م`);
                  setLineItems([{ description: `اشتراك جزئي (${remainingDays} يوم)`, item_type: "subscription", amount: amount.toString(), quantity: 1 }]);
                  setTitle(`اشتراك جزئي - ${child.full_name}`);
                } else {
                  setProrataInfo(null);
                }
              } else {
                setProrataInfo(null);
              }
            }} />
          </div>
          {prorataInfo && (
            <p className="text-xs text-primary bg-primary/5 rounded-md p-2 text-center">{prorataInfo}</p>
          )}

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">بنود الفاتورة</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-3 h-3 ml-1" /> إضافة بند
              </Button>
            </div>
            {lineItems.map((item, idx) => (
              <div key={idx} className="border rounded-lg p-3 space-y-2 bg-muted/30">
                <div className="flex gap-2">
                  <Input
                    placeholder="وصف البند"
                    value={item.description}
                    onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                    className="flex-1"
                  />
                  {lineItems.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(idx)} className="shrink-0 text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={item.item_type} onValueChange={(v) => updateLineItem(idx, "item_type", v)}>
                    <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {itemTypeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="المبلغ"
                    value={item.amount}
                    onChange={(e) => updateLineItem(idx, "amount", e.target.value)}
                    min={0}
                    step={0.01}
                    dir="ltr"
                  />
                  <Input
                    type="number"
                    placeholder="الكمية"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(idx, "quantity", parseInt(e.target.value) || 1)}
                    min={1}
                    dir="ltr"
                  />
                </div>
              </div>
            ))}
            <div className="text-left font-bold text-primary text-sm">
              الإجمالي: {totalAmount.toLocaleString()} ج.م
            </div>
          </div>

          <div>
            <Label>ملاحظات</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ملاحظات إضافية..." rows={2} />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إنشاء الفاتورة"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
